import axios from "axios";
import { getAuthHeaders } from "../firebase/authHeaders";
import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../context/authContext";
import { createCheckouts } from "../firebase/payments";
import { getUserData } from "../firebase/users";
import Button from "../components/commons/Button";
import PlanDialog from "../components/dialogs/PlanDialog";
import { useNavigate } from "react-router-dom";
import { FEE_TIERS, getFeeRate, formatFeeRate } from "../utils/fees";

const Plans = () => {
  const { user, membershipsForUser } = useAuth();
  const [initialData, setUserInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [activeDialogPlan, setActiveDialogPlan] = useState(null);
  const navigate = useNavigate();

  const planLabels = {
    basic: "Basic",
    pro: "Pro",
    elite: "Elite",
  };

  const openPlanDialog = (plan) => {
    setActiveDialogPlan(plan);
    setShowPlanDialog(true);
  };

  const closePlanDialog = () => {
    setShowPlanDialog(false);
    setActiveDialogPlan(null);
  };

  const annualSavingsText = (plan) => {
    const monthly = membershipsForUser[plan]?.price;
    const yearly = membershipsForUser[`${plan}Yearly`]?.price;
    if (!monthly || !yearly) return "Annual billing available.";
    const savings = Math.round((1 - yearly / (monthly * 12)) * 100);
    return savings > 0
      ? `Save ${savings}% with annual billing.`
      : "Annual billing available.";
  };

  useEffect(() => {
    if (user) {
      getUserData(user.uid).then((data) => {
        // const userData = { ...userData, ...data };

        setUserInitialData(data);

        setTimeout(() => {
          setLoading(false);
        }, 200);
      });
    }
  }, [user]);

  const handleMembership = async (newMembership) => {
    if(!user){
      navigate("/login");
      return;
    }
    setLoading(true);

    axios({
      method: "post",
      url: process.env.REACT_APP_PAYMENT_API + "/create-membership-checkout",
      headers: await getAuthHeaders(),
      data: {
        priceId: membershipsForUser[newMembership].priceId,
        prevLink: window.location.href,
        customerId: initialData.customer_id,
      },
    }).then(async ({ data }) => {
      await createCheckouts({
        sessionId: data.session.id,
        sessionLink: data.session.url,
        checkoutInfo: {
          type: "membership",
          plan: newMembership,
          user_id: initialData.id,
          price: membershipsForUser[newMembership].price,
        },
      });
      window.location.href = data.session.url;

      setTimeout(() => {
        setLoading(false);
      }, 200);
    }).catch(async (err) => {
      console.log(err.response.data);
      if(err.response && err.response.data.error && err.response.data.error.startsWith("No such customer")){
        //Customer Id is invalid, so we retry without customer id
        axios({
          method: "post",
          url: process.env.REACT_APP_PAYMENT_API + "/create-membership-checkout",
          headers: await getAuthHeaders(),
          data: {
            priceId: membershipsForUser[newMembership].priceId,
            prevLink: window.location.href,
            // customerId: initialData.customer_id,
          },
        }).then(async ({ data }) => {
          await createCheckouts({
            sessionId: data.session.id,
            sessionLink: data.session.url,
            checkoutInfo: {
              type: "membership",
              plan: newMembership,
              user_id: initialData.id,
              price: membershipsForUser[newMembership].price,
            },
          });
          window.location.href = data.session.url;

          setTimeout(() => {
            setLoading(false);
          }, 200);
        }).catch((err) => {
          alert(err.response.data.error);
        });

      }
      else{
        alert(err.response.data.error);
      }
    });
  };

  const handlePlanPurchase = (membershipKey) => {
    if (!membershipKey) return;
    handleMembership(membershipKey);
  };

  return (
    <Layout>
      <section className="bg-blue py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-extrabold text-white sm:text-5xl">
              Pricing Plans
            </h2>
            <p className="mt-4 text-xl text-gray-400">
              Simple, transparent pricing for your business needs.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {/* basic */}
            <div className="p-8 flex flex-col bg-white/5 backdrop-blur-sm rounded-lg shadow-lg h-full min-h-[520px] relative">
              {initialData?.membership === "basic" && (
                <div className="absolute top-2 right-2 text-white font-semibold flex items-center gap-2 rounded-full bg-primary px-2 py-1 z-10">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"
                    />
                  </svg>
                  Current Plan
                </div>
              )}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-white">Basic</h3>
                <p className="mt-4 text-gray-400">
                  Get started with our basic features.
                </p>
              </div>
              <div className="mb-8">
                <span className="text-5xl font-extrabold text-white">
                  ${membershipsForUser["basic"].price}
                </span>
                <span className="text-xl font-medium text-gray-400"> /month</span>
                <p className="text-sm text-gray-400 mt-2">
                  {annualSavingsText("basic")}
                </p>
              </div>
              <ul className="mb-8 space-y-4 text-gray-400">
                {membershipsForUser["basic"].benefits.map((benefit) => (
                  <li className="flex items-center" key={benefit}>
                    <svg
                      className="h-6 w-6 text-green-500 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto space-y-3">
                {initialData?.membership !== "basic" && (
                  <Button
                    divClassName="bg-primary sm:text-base text-xs w-full"
                    buttonClassName="group-hover:!bg-white group-hover:!bg-none group-hover:text-black sm:!p-3 !p-[10px]"
                    isActive
                    onClick={() => openPlanDialog("basic")}
                  >
                    <div className="flex items-center justify-center w-full">
                      <span className="mr-1">{initialData?.membership === "free" ? "Select Plan" : "Change Plan"}</span>
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="mt-[1px]">
                        <path d="M0.708333 8L0 7.29167L3.29167 4L0 0.708333L0.708333 0L4.70833 4L0.708333 8Z" className="fill-white group-hover:fill-black" />
                      </svg>
                    </div>
                  </Button>
                )}
              </div>
            </div>

            {/* pro */}
            <div className="p-8 flex flex-col bg-white/5 backdrop-blur-sm rounded-lg shadow-lg h-full min-h-[520px] relative">
              {initialData?.membership === "pro" && (
                <div className="absolute top-2 right-2 text-white font-semibold flex items-center gap-2 rounded-full bg-primary px-2 py-1 z-10">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"
                    />
                  </svg>
                  Current Plan
                </div>
              )}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-white">Pro</h3>
                <p className="mt-4 text-gray-400">
                  Ideal for growing businesses and enterprises.
                </p>
              </div>
              <div className="mb-8">
                <span className="text-5xl font-extrabold text-white">
                  ${membershipsForUser["pro"].price}
                </span>
                <span className="text-xl font-medium text-gray-400"> /month</span>
                <p className="text-sm text-gray-400 mt-2">
                  {annualSavingsText("pro")}
                </p>
              </div>
              <ul className="mb-8 space-y-4 text-gray-400">
                {membershipsForUser["pro"].benefits.map((benefit) => (
                  <li className="flex items-center" key={benefit}>
                    <svg
                      className="h-6 w-6 text-green-500 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto space-y-3">
                {initialData?.membership !== "pro" && (
                  <Button
                    divClassName="bg-primary sm:text-base text-xs w-full"
                    buttonClassName="group-hover:!bg-white group-hover:!bg-none group-hover:text-black sm:!p-3 !p-[10px]"
                    isActive
                    onClick={() => openPlanDialog("pro")}
                  >
                    <div className="flex items-center justify-center w-full">
                      <span className="mr-1">{initialData?.membership === "free" ? "Select Plan" : "Change Plan"}</span>
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="mt-[1px]">
                        <path d="M0.708333 8L0 7.29167L3.29167 4L0 0.708333L0.708333 0L4.70833 4L0.708333 8Z" className="fill-white group-hover:fill-black" />
                      </svg>
                    </div>
                  </Button>
                )}
              </div>
            </div>

            {/* elite */}
            <div className="p-8 flex flex-col bg-white/5 backdrop-blur-sm rounded-lg shadow-lg h-full min-h-[520px] relative">
              {initialData?.membership === "elite" && (
                <div className="absolute top-2 right-2 text-white font-semibold flex items-center gap-2 rounded-full bg-primary px-2 py-1 z-10">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"
                    />
                  </svg>
                  Current Plan
                </div>
              )}
              <div className="mb-8">
                <h3 className="text-2xl font-semibold text-white">Elite</h3>
                <p className="mt-4 text-gray-400">Tailored for custom needs.</p>
              </div>
              <div className="mb-8">
                <span className="text-5xl font-extrabold text-white">
                  ${membershipsForUser["elite"].price}
                </span>
                <span className="text-xl font-medium text-gray-400"> /month</span>
                <p className="text-sm text-gray-400 mt-2">
                  {annualSavingsText("elite")}
                </p>
              </div>
              <ul className="mb-8 space-y-4 text-gray-400">
                {membershipsForUser["elite"].benefits.map((benefit) => (
                  <li className="flex items-center" key={benefit}>
                    <svg
                      className="h-6 w-6 text-green-500 mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto space-y-3">
                {initialData?.membership !== "elite" && (
                  <Button
                    divClassName="bg-primary sm:text-base text-xs w-full"
                    buttonClassName="group-hover:!bg-white group-hover:!bg-none group-hover:text-black sm:!p-3 !p-[10px]"
                    isActive
                    onClick={() => openPlanDialog("elite")}
                  >
                    <div className="flex items-center justify-center w-full">
                      <span className="mr-1">{initialData?.membership === "free" ? "Select Plan" : "Change Plan"}</span>
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg" className="mt-[1px]">
                        <path d="M0.708333 8L0 7.29167L3.29167 4L0 0.708333L0.708333 0L4.70833 4L0.708333 8Z" className="fill-white group-hover:fill-black" />
                      </svg>
                    </div>
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* The ladder itself, since a bullet list can't express seven bands
              across three plans. Rendered from utils/fees.js so the quoted
              rates cannot drift from the ones actually charged. */}
          <div className="mt-16">
            <h3 className="text-2xl font-semibold text-white text-center">
              Transaction Fees by Item Price
            </h3>
            <p className="mt-3 max-w-3xl mx-auto text-center text-gray-400">
              Fees are charged per product, against that product&apos;s own price, so a
              high-value card in a basket of singles is still charged its own lower rate.
              Only sellers carry a membership. Buyers need an account to check out and
              pay no platform fee at all.
            </p>

            <div className="mt-8 overflow-x-auto">
              <table className="w-full min-w-[480px] text-left text-gray-300">
                <thead>
                  <tr className="border-b border-white/20 text-sm uppercase tracking-wide text-gray-400">
                    <th className="py-3 pr-4 font-medium">Item Price</th>
                    <th className="py-3 px-4 font-medium">Basic</th>
                    <th className="py-3 px-4 font-medium">Pro</th>
                    <th className="py-3 pl-4 font-medium">Elite</th>
                  </tr>
                </thead>
                <tbody>
                  {FEE_TIERS.map((tier) => (
                    <tr className="border-b border-white/10" key={tier.label}>
                      <td className="py-3 pr-4 text-white whitespace-nowrap">{tier.label}</td>
                      <td className="py-3 px-4">{formatFeeRate(getFeeRate(tier.min, "basic"))}</td>
                      <td className="py-3 px-4">{formatFeeRate(getFeeRate(tier.min, "pro"))}</td>
                      <td className="py-3 pl-4">{formatFeeRate(getFeeRate(tier.min, "elite"))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <PlanDialog
        open={showPlanDialog}
        onClose={closePlanDialog}
        activePlan={activeDialogPlan}
        membershipsForUser={membershipsForUser}
        onConfirm={(membershipKey) => {
          handlePlanPurchase(membershipKey);
          closePlanDialog();
        }}
      />
    </Layout>
  );
};

export default Plans;
